/**
 * SeverityGovernanceView — iter84.61 · Stewardship → Severity.
 *
 * The ONE place severity gets agreed and modified. Renders:
 *   1. The severity definitions (from values_registry.md §3 — source of truth).
 *   2. Every signal/gate with an editable severity (writes back to the
 *      registry via POST /values-registry/severity; audited).
 *   3. Every active obligation with an editable severity (same pattern).
 *   4. Active waivers on high signals, with revoke.
 * Values / Obligations tabs render severity read-only.
 */
import { useCallback, useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SeverityChip } from "@/components/site-admin/ValuesAndUATCards";

const SEVERITIES = ["critical", "high", "med", "low"];

function SeverityEditor({ name, current, meta, onSave, testPrefix }) {
    const [sev, setSev] = useState(current);
    const [busy, setBusy] = useState(false);
    const changed = sev !== current;

    const save = async () => {
        setBusy(true);
        try {
            await onSave(name, sev);
            toast.success(`${name} → ${sev}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
            setSev(current);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="flex items-center gap-2 flex-wrap border border-zinc-800 rounded p-2 text-[11px] bg-black/40"
            data-testid={`${testPrefix}-row-${name}`}
        >
            <code className="font-bold text-white">{name}</code>
            <SeverityChip severity={current} />
            {meta && <span className="text-[9px] text-zinc-600">{meta}</span>}
            <div className="ml-auto flex items-center gap-1.5">
                <select
                    value={sev}
                    disabled={busy}
                    onChange={(e) => setSev(e.target.value)}
                    data-testid={`${testPrefix}-select-${name}`}
                    className="brutal-input text-[10px] py-0.5 px-1.5 bg-zinc-900 border-zinc-700"
                >
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button
                    type="button"
                    disabled={busy || !changed}
                    onClick={save}
                    data-testid={`${testPrefix}-save-${name}`}
                    className="brutal-btn text-[9px] py-0.5 px-2 bg-emerald-600 text-white disabled:opacity-40"
                >
                    Save
                </button>
            </div>
        </div>
    );
}

export function SeverityGovernanceView() {
    const [registry, setRegistry] = useState(null);
    const [obligations, setObligations] = useState(null);
    const [waivers, setWaivers] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        api.releaseOpsValuesRegistry().then(setRegistry)
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load values registry"));
        api.releaseOpsObligationsRegistry().then(setObligations).catch(() => {});
        api.releaseOpsSignalWaivers().then((r) => setWaivers(r.waivers || [])).catch(() => setWaivers([]));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (error) return <div className="text-xs text-rose-300" data-testid="severity-gov-error">{error}</div>;
    if (!registry) return <p className="text-[11px] text-zinc-500 italic" data-testid="severity-gov-loading">Loading…</p>;

    const rows = ["values_engine", "release_ops"].flatMap((root) =>
        ["crew_level", "game_specific"].flatMap((bucket) =>
            (registry[root]?.[bucket] ?? []).filter((r) => r.status === "active")
        )
    );
    const defs = registry.severity_definitions || [];

    const saveSignal = async (name, severity) => {
        await api.releaseOpsValuesRegistrySeverity({ name, severity });
        load();
    };
    const saveObligation = async (name, severity) => {
        await api.releaseOpsObligationsSeverity({ name, severity });
        load();
    };
    const revoke = async (signalId) => {
        try {
            await api.releaseOpsSignalWaiverRevoke(signalId);
            toast.success(`Waiver on ${signalId} revoked`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Revoke failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="severity-governance-view">
            <section className="border-2 border-violet-900/50 bg-violet-950/10 rounded-lg p-4" data-testid="severity-definitions-section">
                <header className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="w-4 h-4 text-violet-300" />
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200">
                        Severity definitions
                    </h3>
                    <span className="text-[9px] text-zinc-600">source: values_registry.md §3 — the crew-agreed enforcement contract</span>
                </header>
                <div className="space-y-1.5">
                    {defs.map((d) => (
                        <div key={d.severity} className="flex items-start gap-2 text-[11px]" data-testid={`severity-def-${d.severity}`}>
                            <SeverityChip severity={d.severity} />
                            <span className="font-bold text-zinc-300 shrink-0">{d.enforcement}</span>
                            <span className="text-zinc-400">{d.definition}</span>
                        </div>
                    ))}
                </div>
            </section>

            <section className="border-2 border-fuchsia-900/50 bg-fuchsia-950/10 rounded-lg p-4" data-testid="severity-signals-section">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200 mb-1">
                    Signals & gates ({rows.length})
                </h3>
                <p className="text-[10px] text-zinc-500 mb-3">
                    The registry is the source of truth — the Values Engine reads severity from here at runtime.
                    Every change is appended to the audit log.
                </p>
                <div className="space-y-1.5">
                    {rows.map((r) => (
                        <SeverityEditor
                            key={r.name}
                            name={r.name}
                            current={r.severity || "med"}
                            meta={`${r.type} · ${r.scope}${r.value_group && r.value_group !== "PENDING" ? ` · ${r.value_group}` : " · pending classification"}`}
                            onSave={saveSignal}
                            testPrefix="severity-signal"
                        />
                    ))}
                </div>
            </section>

            <section className="border-2 border-cyan-900/50 bg-cyan-950/10 rounded-lg p-4" data-testid="severity-obligations-section">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200 mb-1">
                    Obligations ({obligations?.active_obligations?.length ?? 0})
                </h3>
                <p className="text-[10px] text-zinc-500 mb-3">
                    Severity × compliance_state = risk-if-deployed posture (shown on the Release Ops Obligations Engine card).
                </p>
                <div className="space-y-1.5">
                    {(obligations?.active_obligations || []).map((o) => (
                        <SeverityEditor
                            key={o.name}
                            name={o.name}
                            current={o.severity || "med"}
                            meta={`${o.obligation_group} · ${o.compliance_state} · posture: ${o.risk_posture}`}
                            onSave={saveObligation}
                            testPrefix="severity-obligation"
                        />
                    ))}
                </div>
            </section>

            <section className="border-2 border-violet-900/50 bg-violet-950/10 rounded-lg p-4" data-testid="severity-waivers-section">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200 mb-1">
                    Active waivers ({waivers?.length ?? 0})
                </h3>
                <p className="text-[10px] text-zinc-500 mb-3">
                    A failing <code>high</code> signal may be waived with a reason; <code>critical</code> never can.
                    Waivers are granted on the Release Ops Values Engine card and revoked here or there.
                </p>
                {(waivers || []).length === 0 ? (
                    <p className="text-[11px] text-zinc-500 italic" data-testid="severity-waivers-empty">No active waivers.</p>
                ) : (
                    <div className="space-y-1.5">
                        {waivers.map((w) => (
                            <div
                                key={w.id}
                                className="flex items-center gap-2 flex-wrap border border-zinc-800 rounded p-2 text-[11px] bg-black/40"
                                data-testid={`severity-waiver-row-${w.signal_id}`}
                            >
                                <code className="font-bold text-white">{w.signal_id}</code>
                                <span className="text-zinc-400">“{w.reason}”</span>
                                <span className="text-[9px] text-zinc-600 font-mono">{w.actor} · {w.created_at?.slice(0, 10)}</span>
                                <button
                                    type="button"
                                    onClick={() => revoke(w.signal_id)}
                                    data-testid={`severity-waiver-revoke-${w.signal_id}`}
                                    className="ml-auto brutal-btn text-[9px] py-0.5 px-2"
                                >
                                    Revoke
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
